import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import dayjs from 'dayjs';
import { Button, Col, Popover, Row, Statistic, Table, TablePaginationConfig, Typography, message } from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { useWindowSize } from 'usehooks-ts';

import { formatCurrency } from 'utils';
import { unixTimestampMsToLocaleString } from 'utils/date';

import { cancelAuction, deleteAuction, fetchMyAuctions } from 'apis/auction';
import { Auction, AuctionState } from 'models/auction/Auction';

import AuctionProductTag from 'pages/auctions/AuctionDetail/components/AuctionProductTag';
import AuctionListMenu from './AuctionListMenu';

import styles from './AuctionList.module.css';

const { Title } = Typography;
const AUCTION_MENU_PARAM = 'menu';

function AuctionList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { width: windowWidth } = useWindowSize();
  const isMobileView = windowWidth < 1000;

  const activeAuctionMenu = (searchParams.get(AUCTION_MENU_PARAM) ?? 'active') as AuctionState;
  const setActiveAuctionMenu = (newActiveAuctionMenu: AuctionState) => {
    searchParams.set(AUCTION_MENU_PARAM, newActiveAuctionMenu);
    setSearchParams(searchParams);
  };

  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 15,
    showSizeChanger: false,
  });

  const fetchPage = (currentPage: number) => {
    if (isLoading) return;
    setIsLoading(true);

    fetchMyAuctions(currentPage, activeAuctionMenu)
      .then((data) => {
        const fetchedPage = data.pagination.page;
        const fetchedPageSize = data.pagination.per_page;

        setAuctions((auctions) => {
          const newAuctions = [...auctions];
          data.auctions.forEach(
            (auction, index) => (newAuctions[(fetchedPage - 1) * fetchedPageSize + index] = auction)
          );
          return newAuctions;
        });

        setPagination({ ...pagination, total: data.pagination.total, pageSize: fetchedPageSize, current: fetchedPage });
      })
      .catch((errMsg) => {
        if (errMsg) message.error(errMsg);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    setAuctions([]);
    fetchPage(1);
  }, [activeAuctionMenu]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    fetchPage(pagination.current ?? 1);
  };

  const handleClickCancelBtn = (auction: Auction) => {
    if (window.confirm('정말 취소하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      cancelAuction(auction.id)
        .then(() => {
          setActiveAuctionMenu('canceled');
        })
        .catch((errMsg) => {
          if (errMsg) message.error(errMsg);
        });
    }
  };

  const handleClickDeleteBtn = (auction: Auction) => {
    if (window.confirm('정말 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      deleteAuction(auction.id)
        .then(() => {
          message.success('삭제되었습니다.');
          setAuctions((auctions) => auctions.filter(({ id }) => id !== auction.id));
          setPagination((pagination) => ({ ...pagination, total: (pagination.total ?? 1) - 1 }));
        })
        .catch((errMsg) => {
          if (errMsg) message.error(errMsg);
        });
    }
  };

  const handleClickRow = (auction: Auction) => () => {
    const { id, state } = auction;

    if (state === 'draft') {
      navigate(`/auctions/${id}/edit`);
    } else {
      navigate(`/auctions/${id}`);
    }
  };

  const cancelBtn = (auction: Auction) =>
    auction.bid_count === 0 ? (
      <Button
        className="no-padding"
        type="link"
        onClick={(e) => {
          e.stopPropagation(); // prevent bubbling (if not, click event make go to detail page)
          handleClickCancelBtn(auction);
        }}
      >
        취소
      </Button>
    ) : (
      <Popover content="입찰자가 있어 취소할 수 없습니다">
        <Button
          className={`no-padding ${styles.disabled}`}
          type="link"
          onClick={(e) => {
            e.stopPropagation(); // prevent bubbling (if not, click event make go to detail page)
          }}
        >
          취소
        </Button>
      </Popover>
    );

  const deleteBtn = (auction: Auction) => (
    <Button
      className="no-padding"
      type="link"
      onClick={(e) => {
        e.stopPropagation(); // prevent bubbling (if not, click event make go to detail page)
        handleClickDeleteBtn(auction);
      }}
    >
      삭제
    </Button>
  );

  const columnFixedWidths = {
    active: {
      endsAt: 120,
    },
    sold: {
      endsAt: 200,
    },
    unsold: {
      endsAt: 200,
    },
    common: {
      action: 80,
      currentPrice: 120,
    },
  };

  const columns: ColumnsType<Auction> = [
    {
      title: '제목',
      dataIndex: 'title',
      render: (title, auction) => (
        <Row className={styles['title-col']} wrap={false} align="middle">
          <Col>
            <AuctionProductTag condition={auction.condition} />
          </Col>
          <Col>{title || '(이름이 지정되지 않은 경매)'}</Col>
        </Row>
      ),
      fixed: 'left',
      width: Math.min(windowWidth / 2, 320),
    },
  ];

  switch (activeAuctionMenu) {
    case 'active':
      columns.push({
        title: '현재 최고가',
        dataIndex: 'current_price',
        width: columnFixedWidths.common.currentPrice,
        render: (currentPrice) => formatCurrency(currentPrice),
      });
      columns.push({
        title: '남은 시간',
        dataIndex: 'ends_at',
        width: columnFixedWidths.active.endsAt,
        render: (endsAt) => (
          <Statistic.Countdown
            className={styles.countdown}
            value={endsAt}
            format={dayjs.unix(endsAt / 1000).diff(dayjs(), 'day') > 0 ? 'D일 H시간 m분 s초' : 'HH:mm:ss'}
          />
        ),
      });
      columns.push({
        title: 'Action',
        dataIndex: '',
        width: columnFixedWidths.common.action,
        render: (_, auction) => cancelBtn(auction),
      });
      break;
    case 'sold':
      columns.push({
        title: '낙찰가',
        dataIndex: 'current_price',
        width: columnFixedWidths.common.currentPrice,
        render: (currentPrice) => formatCurrency(currentPrice),
      });
      columns.push({
        title: '종료 시각',
        dataIndex: 'ends_at',
        width: columnFixedWidths.sold.endsAt,
        render: (endsAt: string) => unixTimestampMsToLocaleString(endsAt),
      });
      break;
    case 'unsold':
      columns.push({
        title: '종료 시각',
        dataIndex: 'ends_at',
        width: columnFixedWidths.unsold.endsAt,
        render: (endsAt: string) => unixTimestampMsToLocaleString(endsAt),
      });
      break;
    case 'draft':
    case 'canceled':
      columns.push({
        title: 'Action',
        dataIndex: '',
        width: columnFixedWidths.common.action,
        render: (_, auction) => deleteBtn(auction),
      });
      break;
    // TODO: case 'scheduled': 시작까지 남은 시간 + 수정 / 취소 버튼
  }

  return (
    <Row gutter={12} wrap={false}>
      {!isMobileView && (
        <Col flex="0 0 160px">
          <AuctionListMenu
            activeAuctionMenu={activeAuctionMenu}
            setActiveAuctionMenu={setActiveAuctionMenu}
            isMobileView={isMobileView}
          />
        </Col>
      )}
      <Col flex={1}>
        <Row className={styles.header} justify="space-between" align="middle">
          {isMobileView ? (
            <AuctionListMenu
              activeAuctionMenu={activeAuctionMenu}
              setActiveAuctionMenu={setActiveAuctionMenu}
              isMobileView={isMobileView}
            />
          ) : (
            <Title className={styles.title} level={2}>
              Manage {activeAuctionMenu} auctions
            </Title>
          )}
          <Link to="/auctions/new">
            <Button type="primary" icon={<PlusCircleOutlined />}>
              새 경매 생성
            </Button>
          </Link>
        </Row>
        <Table
          size="small"
          columns={columns}
          rowKey={(record) => record.id}
          rowClassName="pointer"
          onRow={(auction) => ({ onClick: handleClickRow(auction) })}
          dataSource={auctions}
          pagination={pagination}
          loading={isLoading}
          onChange={handleTableChange}
          scroll={{ x: 'max-content' }}
        />
      </Col>
    </Row>
  );
}

export default AuctionList;
